import { Card, Col, Row, Select, Space, Spin, Typography } from 'antd';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Outlet, useSearchParams } from 'react-router-dom';

import useAPIQueries from '../../api/useAPIQueries';
import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import OpportunityTabs from '../../components/OpportunityTabs';
import PageContent from '../../components/PageContent';
import SearchParam from '../../components/SearchParam';
import useCanAccessJobPosting from '../../hooks/useCanAccessJobPosting';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { JobPostingId, RecruitmentProcessCollection } from '../../types';
import ConversionFunnel from './ConversionFunnel';
import VacancyCandidateCharts from './VacancyCandidateCharts';
import VacancyInsights from './VacancyInsights';

const ManagerDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams({
    dashboard_status: '',
    matrix_type: 'MATCH',
  });
  const { id } = useTypedParams({ id: JobPostingId });

  useEffect(() => {
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const dashboardStatus: RecruitmentProcessCollection['status'] | undefined = [
    'ACTIVE',
    'INACTIVE',
  ].includes(searchParams.get('dashboard_status') || '')
    ? (searchParams.get('dashboard_status') as RecruitmentProcessCollection['status'])
    : undefined;

  const jobPosting = useAPIQuery([`/job-postings/${id}/`]);

  const stages = useAPIQuery([
    '/stages/',
    {
      params: {
        job_posting_id: id,
        type: 'SUBSCRIBED',
      },
    },
  ]);

  const totalCandidatesQuery = useAPIQuery([
    '/recruitment-processes/',
    {
      params: {
        job_posting: id,
        page_size: 1,
      },
    },
  ]);

  const candidatesInProcess = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          job_posting: id,
          stage__type__in: ['SELECTED', 'CUSTOM', 'APPROVED'].join(','),
          status: dashboardStatus === 'ACTIVE' ? 'ACTIVE' : undefined,
          page_size: 1,
        },
      },
    ],
    { enabled: dashboardStatus !== 'INACTIVE' }
  );

  const totalActiveCandidatesQuery = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          job_posting: id,
          status: 'ACTIVE',
          page_size: 1,
        },
      },
    ],
    {
      enabled: dashboardStatus === 'ACTIVE',
    }
  );

  const totalInactiveCandidatesQuery = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          job_posting: id,
          status: 'INACTIVE',
          page_size: 1,
        },
      },
    ],
    {
      enabled: dashboardStatus === 'INACTIVE',
    }
  );

  const activeCandidatesTestsCompletenessAllStagesPages = useInfiniteAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          status: dashboardStatus !== 'INACTIVE' ? 'ACTIVE' : undefined,
          match__score__isnull: true,
          job_posting: id,
          function_id: jobPosting.data?.function_id ?? undefined,
        },
      },
    ],
    {
      enabled: Boolean(jobPosting.data?.function_id) && !!stages.data?.results.at(0)?.id,
    }
  );

  const activeCandidatesTestsCompletenessAllStages = usePrefetchPages(
    activeCandidatesTestsCompletenessAllStagesPages,
    (data) => data.results
  );

  const activeCandidatesTestsCompletenessSubscribedStage = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          status: dashboardStatus !== 'INACTIVE' ? 'ACTIVE' : undefined,
          match__score__isnull: false,
          job_posting: id,
          stage_id: stages.data?.results.at(0)?.id,
          function_id: jobPosting.data?.function_id || undefined,
        },
      },
    ],
    {
      enabled: Boolean(jobPosting.data?.function_id) && !!stages.data?.results.at(0)?.id,
    }
  );

  //#region ConversionFunnel queries

  const candidateCountByStageWithStatus = useAPIQuery(
    [
      `/job-postings/${id}/stage-counts/`,
      {
        params: {
          candidate_status: dashboardStatus,
        },
      },
    ],
    {
      enabled: Boolean(dashboardStatus),
    }
  );

  const candidateCountByStage = useAPIQuery([`/job-postings/${id}/stage-counts/`]);

  const subscribedStageRecruitmentProcesses = useAPIQuery([
    '/recruitment-processes/',
    {
      params: {
        job_posting: id,
        stage__type: 'SUBSCRIBED',
        page_size: 1,
      },
    },
  ]);

  //#endregion

  // #region VacancyCandidateCharts queries

  const matchScoreRange = useAPIQueries(
    [
      [
        '/recruitment-processes/',
        {
          params: {
            job_posting: id,
            status: dashboardStatus,
            function_id: jobPosting.data?.function_id || undefined,
            match__score__lte: 14.99,
            page_size: 1,
          },
        },
      ],
      [
        '/recruitment-processes/',
        {
          params: {
            job_posting: id,
            status: dashboardStatus,
            function_id: jobPosting.data?.function_id || undefined,
            match__score__gte: 15,
            match__score__lte: 44.99,
            page_size: 1,
          },
        },
      ],
      [
        '/recruitment-processes/',
        {
          params: {
            job_posting: id,
            status: dashboardStatus,
            function_id: jobPosting.data?.function_id || undefined,
            match__score__gte: 45,
            page_size: 1,
          },
        },
      ],
    ],
    {
      select(data) {
        const lowScore = data[0].count;
        const mediumScore = data[1].count;
        const highScore = data[2].count;

        const totalCount = lowScore + mediumScore + highScore;

        return {
          low: (lowScore * 100) / totalCount || 0,
          medium: (mediumScore * 100) / totalCount || 0,
          high: (highScore * 100) / totalCount || 0,
        };
      },
      enabled: Boolean(jobPosting.data?.function_id),
    }
  );

  const ocpScoreRange = useAPIQueries(
    [
      [
        '/recruitment-processes/',
        {
          params: {
            job_posting: id,
            status: dashboardStatus,
            function_id: jobPosting.data?.function_id || undefined,
            ocp__lte: 34.99,
            page_size: 1,
          },
        },
      ],
      [
        '/recruitment-processes/',
        {
          params: {
            job_posting: id,
            status: dashboardStatus,
            function_id: jobPosting.data?.function_id || undefined,
            ocp__gte: 35,
            ocp__lte: 54.99,
            page_size: 1,
          },
        },
      ],
      [
        '/recruitment-processes/',
        {
          params: {
            job_posting: id,
            status: dashboardStatus,
            function_id: jobPosting.data?.function_id || undefined,
            ocp__gte: 55,
            page_size: 1,
          },
        },
      ],
    ],
    {
      select(data) {
        const lowScore = data[0].count;
        const mediumScore = data[1].count;
        const highScore = data[2].count;

        const totalCount = lowScore + mediumScore + highScore;

        return {
          low: (lowScore * 100) / totalCount || 0,
          medium: (mediumScore * 100) / totalCount || 0,
          high: (highScore * 100) / totalCount || 0,
        };
      },
      enabled: Boolean(jobPosting.data?.function_id),
    }
  );

  const rawPotentialScoreRange = useAPIQueries(
    [
      [
        '/recruitment-processes/',
        {
          params: {
            job_posting: id,
            status: dashboardStatus,
            function_id: jobPosting.data?.function_id || undefined,
            raw_potential__lte: 29.99,
            page_size: 1,
          },
        },
      ],
      [
        '/recruitment-processes/',
        {
          params: {
            job_posting: id,
            status: dashboardStatus,
            function_id: jobPosting.data?.function_id || undefined,
            raw_potential__gte: 30,
            raw_potential__lte: 49.99,
            page_size: 1,
          },
        },
      ],
      [
        '/recruitment-processes/',
        {
          params: {
            job_posting: id,
            status: dashboardStatus,
            function_id: jobPosting.data?.function_id || undefined,
            raw_potential__gte: 50,
            page_size: 1,
          },
        },
      ],
    ],
    {
      select(data) {
        const lowScore = data[0].count;
        const mediumScore = data[1].count;
        const highScore = data[2].count;

        const totalCount = lowScore + mediumScore + highScore;

        return {
          low: (lowScore * 100) / totalCount || 0,
          medium: (mediumScore * 100) / totalCount || 0,
          high: (highScore * 100) / totalCount || 0,
        };
      },
      enabled: Boolean(jobPosting.data?.function_id),
    }
  );

  const selectedQuadrantDataSource = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          ...(searchParams.get('selected_ocp') === 'LOW' ? { ocp__lte: 34.99 } : {}),
          ...(searchParams.get('selected_ocp') === 'MEDIUM'
            ? { ocp__gte: 35, ocp__lte: 54.99 }
            : {}),
          ...(searchParams.get('selected_ocp') === 'HIGH' ? { ocp__gte: 55 } : {}),
          ...(searchParams.get('selected_match') === 'LOW' ? { match__score__lte: 14.99 } : {}),
          ...(searchParams.get('selected_match') === 'MEDIUM'
            ? { match__score__gte: 15, match__score__lte: 44.99 }
            : {}),
          ...(searchParams.get('selected_match') === 'HIGH' ? { match__score__gte: 45 } : {}),
          ...(searchParams.get('selected_raw_potential') === 'LOW'
            ? { raw_potential__lte: 29.99 }
            : {}),
          ...(searchParams.get('selected_raw_potential') === 'MEDIUM'
            ? { raw_potential__gte: 30, raw_potential__lte: 49.99 }
            : {}),
          ...(searchParams.get('selected_raw_potential') === 'HIGH'
            ? { raw_potential__gte: 50 }
            : {}),
          function_id: jobPosting.data?.function_id || undefined,
          job_posting: id,
          page_size: 8,
          page: parseInt(searchParams.get('selected_page') || '1', 10),
          status: dashboardStatus,
        },
      },
    ],
    {
      enabled: Boolean(
        jobPosting.data?.function_id &&
          (searchParams.get('selected_ocp') ||
            searchParams.get('selected_match') ||
            searchParams.get('selected_raw_potential'))
      ),
    }
  );

  const ocpMatchMatrix = useAPIQueries(
    [
      // OCP: LOW | MATCH: LOW
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__lte: 34.99,
            match__score__lte: 14.99,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: MEDIUM | MATCH: LOW
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__gte: 35,
            ocp__lte: 54.99,
            match__score__lte: 14.99,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: HIGH | MATCH: LOW
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__gte: 55,
            match__score__lte: 14.99,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: LOW | MATCH: MEDIUM
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__lte: 34.99,
            match__score__gte: 15,
            match__score__lte: 44.99,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: MEDIUM | MATCH: MEDIUM
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__gte: 35,
            ocp__lte: 54.99,
            match__score__gte: 15,
            match__score__lte: 44.99,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: HIGH | MATCH: MEDIUM
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__gte: 55,
            match__score__gte: 15,
            match__score__lte: 44.99,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: LOW | MATCH: HIGH
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__lte: 34.99,
            match__score__gte: 45,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: MEDIUM | MATCH: HIGH
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__gte: 35,
            ocp__lte: 54.99,
            match__score__gte: 45,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: HIGH | MATCH: HIGH
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__gte: 55,
            match__score__gte: 45,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
    ],
    {
      select(data) {
        const countArray = data.map(({ count }) => count);
        return [
          [countArray[0], countArray[1], countArray[2]],
          [countArray[3], countArray[4], countArray[5]],
          [countArray[6], countArray[7], countArray[8]],
        ];
      },
      enabled: Boolean(jobPosting.data?.function_id),
    }
  );

  const ocpRawPotentialMatrix = useAPIQueries<'/recruitment-processes/', 'GET', number[][]>(
    [
      // OCP: LOW | RAW_POTENTIAL: LOW
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__lte: 34.99,
            raw_potential__lte: 29.99,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: MEDIUM | RAW_POTENTIAL: LOW
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__gte: 35,
            ocp__lte: 54.99,
            raw_potential__lte: 29.99,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: HIGH | RAW_POTENTIAL: LOW
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__gte: 55,
            raw_potential__lte: 29.99,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: LOW | RAW_POTENTIAL: MEDIUM
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__lte: 34.99,
            raw_potential__gte: 30,
            raw_potential__lte: 49.99,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: MEDIUM | RAW_POTENTIAL: MEDIUM
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__gte: 35,
            ocp__lte: 54.99,
            raw_potential__gte: 30,
            raw_potential__lte: 49.99,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: HIGH | RAW_POTENTIAL: MEDIUM
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__gte: 55,
            raw_potential__gte: 30,
            raw_potential__lte: 49.99,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: LOW | RAW_POTENTIAL: HIGH
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__lte: 34.99,
            raw_potential__gte: 50,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: MEDIUM | RAW_POTENTIAL: HIGH
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__gte: 35,
            ocp__lte: 54.99,
            raw_potential__gte: 50,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
      // OCP: HIGH | RAW_POTENTIAL: HIGH
      [
        '/recruitment-processes/',
        {
          params: {
            ocp__gte: 55,
            raw_potential__gte: 50,
            function_id: jobPosting.data?.function_id || undefined,
            job_posting: id,
            status: dashboardStatus,
            page_size: 1,
          },
        },
      ],
    ],
    {
      select(data) {
        const countArray = data.map(({ count }) => count);
        return [
          [countArray[0], countArray[1], countArray[2]],
          [countArray[3], countArray[4], countArray[5]],
          [countArray[6], countArray[7], countArray[8]],
        ];
      },
      enabled: Boolean(jobPosting.data?.function_id),
    }
  );

  const { canManage } = useCanAccessJobPosting({ jobPostingId: id });

  // #endregion

  return (
    <ErrorResult
      error={
        jobPosting.error ||
        stages.error ||
        candidatesInProcess.error ||
        totalCandidatesQuery.error ||
        totalActiveCandidatesQuery.error ||
        totalInactiveCandidatesQuery.error ||
        activeCandidatesTestsCompletenessAllStagesPages.error ||
        activeCandidatesTestsCompletenessSubscribedStage.error ||
        candidateCountByStageWithStatus.error ||
        candidateCountByStage.error ||
        subscribedStageRecruitmentProcesses.error ||
        matchScoreRange.error ||
        ocpScoreRange.error ||
        rawPotentialScoreRange.error ||
        selectedQuadrantDataSource.error ||
        ocpMatchMatrix.error ||
        ocpRawPotentialMatrix.error
      }
    >
      <Spin
        spinning={
          jobPosting.isLoading ||
          stages.isLoading ||
          candidatesInProcess.isLoading ||
          totalCandidatesQuery.isLoading ||
          totalActiveCandidatesQuery.isLoading ||
          totalInactiveCandidatesQuery.isLoading ||
          activeCandidatesTestsCompletenessAllStagesPages.isLoading ||
          activeCandidatesTestsCompletenessSubscribedStage.isLoading ||
          candidateCountByStageWithStatus.isLoading ||
          candidateCountByStage.isLoading ||
          subscribedStageRecruitmentProcesses.isLoading ||
          matchScoreRange.isLoading ||
          ocpScoreRange.isLoading ||
          rawPotentialScoreRange.isLoading ||
          selectedQuadrantDataSource.isLoading ||
          ocpMatchMatrix.isLoading ||
          ocpRawPotentialMatrix.isLoading
        }
      >
        <Helmet>
          <title>
            Dashboard da vaga | {jobPosting.data?.name || '...'} | Recrutamento e Seleção
          </title>
        </Helmet>
        <PageContent>
          <Outlet />
          <Card>
            <Space direction="vertical" size="middle">
              <OpportunityTabs />
              <Row justify="space-between">
                <Col>
                  <Typography.Title level={4}>Insights da Vaga</Typography.Title>
                </Col>
                <Col>
                  <Typography.Text strong>Status: </Typography.Text>
                  <SearchParam name="dashboard_status" defaultValue="">
                    <Select style={{ width: 120 }}>
                      <Select.Option key="statusAccumulated" value="">
                        Acumulado
                      </Select.Option>
                      <Select.Option key="statusActive" value="ACTIVE">
                        Ativos
                      </Select.Option>
                      <Select.Option key="statusInactive" value="INACTIVE">
                        Reprovados
                      </Select.Option>
                    </Select>
                  </SearchParam>
                </Col>
              </Row>
              <VacancyInsights
                finalizedTestsCandidateIdsAtSubscription={
                  activeCandidatesTestsCompletenessSubscribedStage.data?.count
                }
                missingTestsCandidateIdsAllStages={activeCandidatesTestsCompletenessAllStages.map(
                  ({ candidate }) => candidate.id
                )}
                jobPosting={jobPosting.data}
                canManage={canManage}
                dashboardStatus={dashboardStatus}
                totalCandidatesInProcess={candidatesInProcess.data?.count}
                totalCandidateCount={totalCandidatesQuery.data?.count}
                totalActiveCandidateCount={totalActiveCandidatesQuery.data?.count}
                totalInactiveCandidateCount={totalInactiveCandidatesQuery.data?.count}
                totalCandidateMissingTestCount={
                  activeCandidatesTestsCompletenessAllStagesPages.data?.pages.at(0)?.count
                }
              />
              {dashboardStatus === 'ACTIVE' ? (
                <div>
                  <Typography.Title level={4}>Funil Atual</Typography.Title>
                  <Typography.Paragraph>
                    O funil atual apresenta quantos candidatos ativos estão em cada etapa neste
                    momento e a porcentagem em relação ao total de candidatos que passaram por
                    aquela etapa. A visão do funil pode ser alterada de acordo com o filtro de
                    status dos candidatos localizado no canto superior direito da página.
                  </Typography.Paragraph>
                </div>
              ) : dashboardStatus === 'INACTIVE' ? (
                <div>
                  <Typography.Title level={4}>Funil de Reprovação</Typography.Title>
                  <Typography.Paragraph>
                    O funil de reprovação apresenta quantos candidatos foram reprovados em cada
                    etapa e a porcentagem em relação ao total de candidatos que passaram por aquela
                    etapa. A visão do funil pode ser alterada de acordo com o filtro de status dos
                    candidatos localizado no canto superior direito da página.
                  </Typography.Paragraph>
                </div>
              ) : (
                <div>
                  <Typography.Title level={4}>Funil de Conversão</Typography.Title>
                  <Typography.Paragraph>
                    O funil de conversão apresenta a quantidade acumulada de todos os candidatos da
                    vaga que passaram por aquela etapa, ou seja ativos e reprovados, assim como a
                    porcentagem de conversão de etapa para etapa. A visão do funil pode ser alterada
                    de acordo com o filtro de status dos candidatos localizado no canto superior
                    direito da página.
                  </Typography.Paragraph>
                </div>
              )}
              <ConversionFunnel
                totalVacancyPositions={jobPosting.data?.positions ?? 0}
                totalCandidates={totalCandidatesQuery.data?.count}
                dashboardStatus={dashboardStatus}
                recruitmentProcessesCountByStage={candidateCountByStage.data}
                statusRecruitmentProcessesCountByStage={candidateCountByStageWithStatus.data}
                subscribedCount={subscribedStageRecruitmentProcesses.data?.count}
              />
              <Typography.Title level={4} style={{ marginTop: 20 }}>
                Candidatos da Vaga
              </Typography.Title>
              <VacancyCandidateCharts
                dashboardStatus={dashboardStatus}
                matchPercentage={matchScoreRange.data}
                ocpPercentage={ocpScoreRange.data}
                rawPotentialPercentage={rawPotentialScoreRange.data}
                ocpMatchMatrix={
                  ocpMatchMatrix.data ?? [
                    [0, 0, 0],
                    [0, 0, 0],
                    [0, 0, 0],
                  ]
                }
                ocpRawPotentialMatrix={
                  ocpRawPotentialMatrix.data ?? [
                    [0, 0, 0],
                    [0, 0, 0],
                    [0, 0, 0],
                  ]
                }
                selectedQuadrantDataSource={selectedQuadrantDataSource.data?.results}
                selectedCount={selectedQuadrantDataSource.data?.count}
                loading={selectedQuadrantDataSource.isFetching}
              />
            </Space>
          </Card>
        </PageContent>
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(ManagerDashboard);
